// Enrollment site config types — the render contract for the public enroll app
// (apps/group-enrollment) and the edit model for the internal site builder.
// Moved from packages/client-components/src/pages/public/enroll/types.ts so the
// API can validate and store configs; that path re-exports from here.

export type PayPeriod = 'weekly' | 'biweekly' | 'semimonthly' | 'monthly';

export type ProductCategory =
  | 'medical'
  | 'limited-medical'
  | 'dental'
  | 'vision'
  | 'life'
  | 'disability'
  | 'critical-illness';

export type CoverageTier = 'employee' | 'employeeSpouse' | 'employeeChildren' | 'family';

export interface BenefitRow {
  category: string;
  label: string;
  values: Record<string, string | null>;
}

export interface DetailRow {
  label: string;
  value: string;
}

export interface PlanConfig {
  id: string;
  category: ProductCategory;
  name: string;
  tagline?: string;
  highlights: string[];
  rates: Record<CoverageTier, number>;
  network?: string;
  sbcUrl?: string;
  limitedBenefit?: boolean;
  accentColor: string;
  detailRows?: DetailRow[];
}

export interface ProductConfig {
  category: ProductCategory;
  label: string;
  description: string;
  planIds: string[];
  declinable: boolean;
  declineStyle?: 'declined' | 'skipped';
  note?: string;
  /** Defaults to true. Set false for employee-only products (e.g. STD) so no tier picker shows. */
  tierSelectable?: boolean;
  /** Hides the "Lowest cost" badge on this product's plan cards. */
  hideLowestCostBadge?: boolean;
  collectBeneficiary?: boolean;
  // Render inside another step instead of its own: the Extra Protection step, or inline under a category.
  inlineWith?: 'protection' | ProductCategory;
  sectionLabel?: string;
  benefitRows?: BenefitRow[];
  benefitsTableTitle?: string;
  videoUrl?: string;
  videoWidthPercent?: number;
}

export interface HeadlineSegment {
  text: string;
  accent?: boolean;
}

// Generic coverage-card glyph pool (cosmetic, not semantic) — authors pick a glyph
// and a color independently. Keep in sync with COVERAGE_ICON_POOL in coverageIcons.
export type CoverageIcon =
  | 'stethoscope'
  | 'shield-check'
  | 'heart-pulse'
  | 'eye'
  | 'pill'
  | 'hard-hat'
  | 'activity'
  | 'smile'
  | 'umbrella'
  | 'heart-handshake'
  | 'video'
  | 'flask-conical'
  | 'hospital'
  | 'phone'
  | 'piggy-bank';

export interface CoverageCard {
  icon: CoverageIcon;
  /** Accent color for the card icon, a hex from the builder's color pool. */
  color: string;
  title: string;
  bullets: string[];
}

/** Landing "Real care, when you need it" card. `{phone}` in `body` renders as the group's supportPhone. */
export interface CareCard {
  icon: CoverageIcon;
  color: string;
  title: string;
  body: string;
}

export interface LandingResource {
  icon: 'resources' | 'guide' | 'notices' | 'search' | 'pharmacy' | 'video' | 'calendar' | 'website';
  title: string;
  description: string;
  href: string;
  external?: boolean;
}

// The landing "Want to read up first?" block. Optional per group — only groups
// with downloadable guides/resources render it (e.g. cornerstone, not hardhat).
export interface ResourcesSection {
  /** Block heading; defaults to "Want to read up first?". */
  title?: string;
  intro: string;
  items: LandingResource[];
}

export interface ResourceDoc {
  title: string;
  description: string;
  href: string;
}

/** `text` supports renderRichText inline tags (<url>/<tel>/<email>). */
export interface ResourceDisclosure {
  label: string;
  text: string;
}

/** Standalone "Plan Resources" page. Optional per group — absent groups fall back to a placeholder. */
export interface ResourcesPageContent {
  /** Page heading; defaults to "Plan Resources". */
  title?: string;
  intro: string;
  documents: ResourceDoc[];
  /** Highlight card (named for its first use). Omitted = DEFAULT_HIGHLIGHT, null = none. */
  virtualCare?: HighlightCard | null;
  /** Omitted = the shared DEFAULT_DISCLOSURES; set to override per site. */
  disclosures?: ResourceDisclosure[];
}

/** Card shown beside a page's disclosures. Icon defaults to the stethoscope. */
export interface HighlightCard {
  icon?: CoverageIcon;
  title: string;
  body: string;
  closing?: string;
}

/** Standalone "Annual Notices" page (/{slug}/notices). Optional per group. */
export interface NoticesPageContent {
  /** Page heading; defaults to "Annual Notices". */
  title?: string;
  intro: string;
  documents: ResourceDoc[];
  /** Omitted = DEFAULT_HIGHLIGHT, null = none. */
  highlight?: HighlightCard | null;
  /** Omitted = the shared DEFAULT_DISCLOSURES. */
  disclosures?: ResourceDisclosure[];
}

/** `answer`, `bullets`, and `closing` support renderRichText inline tags (<url>/<tel>/<email>). */
export interface FaqItem {
  question: string;
  answer: string;
  bullets?: string[];
  closing?: string;
}

export interface FaqSection {
  label: string;
  items: FaqItem[];
}

/** `body` and `tip` support renderRichText inline tags (<url>/<tel>/<email>). */
export interface HowToStep {
  title: string;
  body: string;
  tip?: string;
}

export interface HowToEnrollContent {
  subtitle: string;
  steps: HowToStep[];
}

export interface ConfirmationNextStep {
  title: string;
  body: string;
}

export interface ConfirmationFaqItem {
  question: string;
  answer: string;
}

/**
 * Copy for the post-enrollment confirmation receipt. Scoped under `confirmation` so it stays
 * distinct from the standalone FAQ page's content (the separate `faq` field).
 */
export interface ConfirmationContent {
  /** Optional note about when coverage takes effect, shown on the receipt when any plan is elected. */
  effectiveDateNote?: string;
  whatHappensNext: ConfirmationNextStep[];
  faq: ConfirmationFaqItem[];
}

/**
 * Group-dependent checkout fields. Absent fields fall back to the shared defaults (no date of
 * hire, "Phone" label, no SMS consent), so groups only opt in to what their plan requires.
 */
export interface CheckoutConfig {
  collectDateOfHire?: boolean;
  phoneLabel?: string;
  smsConsentDisclosure?: string;
}

/** Copy for the access-code gate screen — the entry point to the enroll flow. */
export interface AccessCodeContent {
  /** Welcome headline; supports the accent-segment pattern. */
  headline: HeadlineSegment[];
  blurb: string;
  /** Placeholder for the code input, e.g. "e.g., 10431". Unused in dropdown mode. */
  placeholder: string;
  entryMode?: AccessCodeEntryMode;
}

export type AccessCodeEntryMode = 'code' | 'dropdown';

export interface GroupConfig {
  slug: string;
  name: string;
  logoUrl: string;
  primaryColor: string;
  heroHeadline: HeadlineSegment[];
  heroTagline: string;
  heroBlurb: string;
  accessCodeScreen: AccessCodeContent;
  /** Defaults to true. When false, the flow skips the access-code gate (public group). */
  requiresAccessCode?: boolean;
  /** Demo/sales site: skips the gate, stubs the submission (nothing posts to e123), shows the fill helper. */
  isDemo?: boolean;
  /** Optional full-width banner under the header */
  banner?: string;
  accessCodeTerm?: string;
  /** Overrides the "HR rep / HR office" contact wording (e.g. "local office"). Unset = original text. */
  benefitsContact?: string;
  coveragePeriod: string;
  supportPhone: string;
  /** SMS and email contact channels shown alongside `supportPhone` in the FAQ page's contact card. */
  supportText?: string;
  supportEmail?: string;
  comparisonPdfUrl?: string;
  landingVideoUrl?: string;
  products: ProductConfig[];
  plans: PlanConfig[];
  coverageCards: CoverageCard[];
  /** Omitted = the shared DEFAULT_CARE_CARDS; set to override per site. */
  careCards?: CareCard[];
  resourcesSection?: ResourcesSection;
  /** Standalone "Plan Resources" page content. Distinct from `resourcesSection` (the landing block). */
  resourcesPage?: ResourcesPageContent;
  noticesPage?: NoticesPageContent;
  /** Hides the landing resource cards and both resource pages from members; content is kept. */
  resourcesHidden?: boolean;
  /** How often members pay; rates are entered per pay period. Omitted = weekly. */
  payPeriod?: PayPeriod;
  confirmation: ConfirmationContent;
  checkout?: CheckoutConfig;
  /**
   * Standalone FAQ page sections. Distinct from `confirmation.faq` (the post-enrollment receipt).
   * Omitted = the shared DEFAULT_FAQ_SECTIONS; set to override per site.
   */
  faq?: FaqSection[];
  /** "How to enroll" page content. Step 2's medical plan list is derived from `plans`, not stored here. */
  howToEnroll?: HowToEnrollContent;
}
export const COVERAGE_TIER_LABELS: Record<CoverageTier, string> = {
  employee: 'Employee Only',
  employeeSpouse: 'Employee + Spouse',
  employeeChildren: 'Employee + Child(ren)',
  family: 'Family',
};

export const BENEFIT_CATEGORY_LABELS: Record<string, string> = {
  preventive: 'Preventive Care',
  everyday: 'Everyday Care',
  prescriptions: 'Prescriptions',
  specialty: 'Specialty & Wellness',
  hospital: 'Hospital & Emergency',
  basic: 'Basic Services',
  major: 'Major Services',
  materials: 'Hardware & Materials',
  plan: 'Plan Details',
};

export const BENEFIT_CATEGORY_COLORS: Record<string, string> = {
  preventive: '#16a34a',
  everyday: '#2563eb',
  prescriptions: '#7c3aed',
  specialty: '#db2777',
  hospital: '#ea580c',
  basic: '#0d9488',
  major: '#b45309',
  materials: '#0891b2',
  plan: '#4f46e5',
};

export const PROTECTION_CATEGORIES: ProductCategory[] = ['life', 'disability', 'critical-illness'];

export function groupOffersProtection(group: GroupConfig): boolean {
  return group.products.some((product) => product.inlineWith === 'protection');
}

/** Whether any elected product asks for a beneficiary. Shared by checkout and the server. */
export function requiresBeneficiary(group: GroupConfig, electedCategories: Iterable<string>): boolean {
  const elected = new Set(electedCategories);
  return group.products.some((product) => product.collectBeneficiary && elected.has(product.category));
}

/** Regular medical, as opposed to a limited-medical-only site. */
export function groupOffersMedical(group: GroupConfig): boolean {
  return group.products.some((product) => product.category === 'medical');
}

export function groupRequiresAccessCode(group: GroupConfig): boolean {
  return group.requiresAccessCode !== false;
}

export function accessCodeEntryMode(group: GroupConfig): AccessCodeEntryMode {
  return group.accessCodeScreen?.entryMode ?? 'code';
}

/** Server-only; never returned by a public procedure. PDIDs are group-global. */
export interface E123Config {
  /** e123 billing period, set from the site's pay period (see PAY_PERIODS). */
  periodId: number;
  /** mm/dd/yyyy */
  effectiveDate: string;
  /** FE plan.id → e123 PDID. Every site plan needs an entry to publish. */
  planPdid: Record<string, number>;
  declinePdid?: number;
  /** Declination pricing period; defaults to periodId. */
  declinePeriodId?: number;
  /** Public (no-access-code) groups only. Code-gated groups resolve the agent from enrollment_location. */
  agentId?: string;
}
